// Date-filter helpers shared by the global "due date" filter and the boards
// that consume it (JobKanbanView on the shop floor, CSMWriteBack in manager
// mode). Extracted from those components so the timezone-sensitive logic is
// unit-testable — the boards themselves are Syncfusion-heavy and awkward to
// exercise directly.
//
// The whole point of these helpers is that every date is treated as a LOCAL
// calendar day. The previous inline code built preset strings with
// `new Date().toISOString().slice(0, 10)`, which is the UTC day — so for users
// west of UTC (e.g. US Eastern), clicking "Today" in the evening produced
// tomorrow's date and the board filtered out the jobs actually due today.

const pad = (n: number): string => String(n).padStart(2, "0");

/** Local calendar day of a Date as `YYYY-MM-DD` (NOT the UTC day). */
export const localDateKey = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// `YYYY-MM-DD`, alone or followed by a time part, which is not read.
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/;

/**
 * A calendar date from the server as that day on this device, at local midnight. Null for a
 * blank value, for text that is not `YYYY-MM-DD`, and for a day that does not exist.
 *
 * The server writes a date that has no time as `2027-07-01T00:00:00`, and `2027-07-01` means
 * the same day. `new Date(text)`, which is what `formatDate` does with text, reads the
 * date-only form and any form ending in `Z` as midnight UTC, so west of Greenwich the screen
 * shows the day before. This reads the three numbers and nothing else: a time or an offset
 * after them is ignored, because the day was never a moment.
 *
 * The inverse of `localDateKey`. Pass the result to `formatDate`, never the text.
 */
export const parseCalendarDate = (value: string | null | undefined): Date | null => {
  const match = CALENDAR_DATE.exec((value ?? "").trim());
  if (!match) return null;

  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(year, month - 1, day);

  // The constructor rolls a day that does not exist into the next month (30 February becomes
  // 2 March) and reads a year under 100 as 19xx, which is what .NET's unset date, year 1,
  // would become. Either way the parts no longer match what was written.
  const asWritten =
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

  return asWritten ? date : null;
};

/** `[from, to]` for the "Today" preset — a single local day. */
export const todayRange = (now: Date = new Date()): [string, string] => {
  const key = localDateKey(now);
  return [key, key];
};

/** `[from, to]` for the "This Week" preset — local Sunday through Saturday. */
export const thisWeekRange = (now: Date = new Date()): [string, string] => {
  const start = new Date(now);
  start.setDate(start.getDate() - start.getDay()); // back to Sunday (local)
  const end = new Date(start);
  end.setDate(start.getDate() + 6); // forward to Saturday
  return [localDateKey(start), localDateKey(end)];
};

/**
 * The calendar day after `dateKey` (`YYYY-MM-DD`), as an exclusive upper bound
 * for range queries. Uses UTC internally purely as a stable calendar (no local
 * offset ever enters the result), so it can't shift like a toISOString() of a
 * locally-constructed Date.
 */
export const nextDayKey = (dateKey: string): string => {
  const [y, m, d] = dateKey.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(
    next.getUTCDate()
  )}`;
};

/**
 * Is `scheduledEndDate` within the inclusive [from, to] local-day range?
 * `from`/`to` are `YYYY-MM-DD` calendar days; the job date is compared by its
 * own local calendar day, so a job due late on the final day still matches.
 */
export const jobInDateRange = (
  scheduledEndDate: string | Date | undefined,
  from: string,
  to: string
): boolean => {
  if (!scheduledEndDate) return false;
  const end =
    scheduledEndDate instanceof Date
      ? scheduledEndDate
      : new Date(scheduledEndDate);
  if (isNaN(end.getTime())) return false;
  const key = localDateKey(end);
  return key >= from && key <= to;
};
